import { defineStore } from 'pinia'
import { ref } from 'vue'
import client from '../api/client.js'

const CAMPAIGNS_KEY = 'avocash.saveToBuy.campaigns.v1'
const DEMANDS_KEY = 'avocash.saveToBuy.demands.v1'

/**
 * Save-To-Buy store — wired to the avocash backend marketplace/savetobuy
 * module (SaveToBuyCampaignController / SaveToBuyParticipantController /
 * SaveToBuyContributionController / ProductDemandRequestController).
 *
 * Enum codes match the Java enums verbatim:
 *   SaveToBuyPaymentMode   : COMMITMENT_ONLY | ESCROW | INSTALLMENT
 *   SaveToBuyStatus        : DRAFT | ACTIVE | TARGET_REACHED | ALLOCATING
 *                             | SUCCESSFUL | EXPIRED | CANCELLED
 *   SaveToBuyParticipantStatus: JOINED | COMMITTED | ESCROWED | COMPLETED
 *                              | REFUNDED | BOUGHT_ALONE | CANCELLED
 *   SaveToBuyContributionStatus: PENDING | PAID | OVERDUE | REFUNDED
 *   SaveToBuyFailurePolicy : REFUND_ALL | BUY_ALONE_AT_NORMAL_PRICE
 *                            | EXTEND_ONCE | PARTIAL_TARGET_WITH_NEW_PRICE
 */

export const PAYMENT_MODES = [
  { code: 'COMMITMENT_ONLY', labelKey: 'saveToBuy.paymentCommitment' },
  { code: 'ESCROW', labelKey: 'saveToBuy.paymentEscrow' },
  { code: 'INSTALLMENT', labelKey: 'saveToBuy.paymentInstallments' },
]

export const FAILURE_POLICIES = [
  { code: 'REFUND_ALL', labelKey: 'saveToBuy.failureRefundAll' },
  { code: 'BUY_ALONE_AT_NORMAL_PRICE', labelKey: 'saveToBuy.failureBuyAlone' },
  { code: 'EXTEND_ONCE', labelKey: 'saveToBuy.failureExtend' },
  {
    code: 'PARTIAL_TARGET_WITH_NEW_PRICE',
    labelKey: 'saveToBuy.failureNegotiate',
  },
]

export const SOURCE_TYPES = {
  CATALOGUE_PRODUCT: 'CATALOGUE_PRODUCT',
  DEMAND_REQUEST: 'DEMAND_REQUEST',
  SELLER_PROPOSAL: 'SELLER_PROPOSAL',
  EXISTING_GROUP: 'EXISTING_GROUP',
}

export const CAMPAIGN_STATUS_META = {
  DRAFT: { labelKey: 'saveToBuy.statusDraft', color: '#8e8e93' },
  ACTIVE: { labelKey: 'saveToBuy.statusActive', color: '#0e7490' },
  TARGET_REACHED: {
    labelKey: 'saveToBuy.statusTargetReached',
    color: '#1a6b42',
  },
  ALLOCATING: { labelKey: 'saveToBuy.statusAllocating', color: '#1a6b42' },
  SUCCESSFUL: { labelKey: 'saveToBuy.statusSuccessful', color: '#1a6b42' },
  EXPIRED: { labelKey: 'saveToBuy.statusExpired', color: '#c0392b' },
  CANCELLED: { labelKey: 'saveToBuy.statusCancelled', color: '#8e8e93' },
}

export const PARTICIPANT_STATUS_META = {
  JOINED: { labelKey: 'saveToBuy.participantJoined', color: '#0e7490' },
  COMMITTED: { labelKey: 'saveToBuy.participantCommitted', color: '#b25e09' },
  ESCROWED: { labelKey: 'saveToBuy.participantEscrowed', color: '#b25e09' },
  COMPLETED: { labelKey: 'saveToBuy.participantCompleted', color: '#1a6b42' },
  REFUNDED: { labelKey: 'saveToBuy.participantRefunded', color: '#8e8e93' },
  BOUGHT_ALONE: { labelKey: 'saveToBuy.participantBoughtAlone', color: '#8e8e93' },
  CANCELLED: { labelKey: 'saveToBuy.participantCancelled', color: '#8e8e93' },
}

export const CONTRIBUTION_STATUS_META = {
  PENDING: { labelKey: 'saveToBuy.contributionPending', color: '#b25e09' },
  PAID: { labelKey: 'saveToBuy.contributionPaid', color: '#1a6b42' },
  OVERDUE: { labelKey: 'saveToBuy.contributionOverdue', color: '#c0392b' },
  REFUNDED: { labelKey: 'saveToBuy.contributionRefunded', color: '#8e8e93' },
}

export const INSTALLMENTS_DEFAULT_MONTHS = 3

/** Suggest default quantity-based price tiers from a base unit price. */
export function suggestTiers(basePrice) {
  const price = Number(basePrice) || 0
  return [
    { minimumQuantity: 1, unitPrice: price, sortOrder: 1 },
    { minimumQuantity: 5, unitPrice: Math.round(price * 0.92), sortOrder: 2 },
    { minimumQuantity: 10, unitPrice: Math.round(price * 0.85), sortOrder: 3 },
  ]
}

/** Best available unit price for a given cumulative quantity. */
export function tierUnitPrice(tiers, quantity) {
  const sorted = [...(tiers || [])].sort((a, b) => a.minimumQuantity - b.minimumQuantity)
  const matched = [...sorted].reverse().find((t) => quantity >= t.minimumQuantity)
  return matched ? matched.unitPrice : sorted[0]?.unitPrice
}

export function offerPrice(offer) {
  const value = offer?.price ?? offer?.priceCfa ?? offer?.currentPrice ?? null
  return value == null || value === '' ? null : Number(value)
}

function load(key) {
  try {
    return JSON.parse(localStorage.getItem(key)) || []
  } catch {
    return []
  }
}

function persist(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // storage unavailable — in-memory only
  }
}

function localId(prefix) {
  return `${prefix}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
}

function isNetworkError(error) {
  return !!error && !error.response
}

export const useSaveToBuyStore = defineStore('saveToBuy', () => {
  const campaigns = ref(load(CAMPAIGNS_KEY))
  const demandRequests = ref(load(DEMANDS_KEY))
  const activeCampaign = ref(null)
  const activeParticipant = ref(null)
  const myParticipations = ref([])
  const contributions = ref([])
  const requestIds = ref(new Set())
  const loading = ref(false)

  function upsertCampaign(campaign) {
    if (!campaign?.id) return
    const index = campaigns.value.findIndex((c) => c.id === campaign.id)
    if (index === -1) campaigns.value.unshift(campaign)
    else campaigns.value[index] = campaign
    persist(CAMPAIGNS_KEY, campaigns.value)
  }

  function upsertDemand(demand) {
    if (!demand?.id) return
    const index = demandRequests.value.findIndex((r) => r.id === demand.id)
    if (index === -1) demandRequests.value.unshift(demand)
    else demandRequests.value[index] = { ...demandRequests.value[index], ...demand }
    persist(DEMANDS_KEY, demandRequests.value)
  }

  // ── Selectors ────────────────────────────────────────────────────

  function byGroup(groupId) {
    return campaigns.value.filter((c) => String(c.sourceGroupId) === String(groupId))
  }

  function campaignById(id) {
    return campaigns.value.find((c) => c.id === id) || null
  }

  // ── Campaign lifecycle ───────────────────────────────────────────

  async function getCampaign(id) {
    loading.value = true
    try {
      try {
        const { data } = await client.get(`save-to-buy/campaigns/${id}`)
        upsertCampaign(data)
        activeCampaign.value = data
        return data
      } catch (error) {
        if (!isNetworkError(error)) throw error
        const local = campaignById(id)
        if (local) {
          activeCampaign.value = local
          return local
        }
        throw error
      }
    } finally {
      loading.value = false
    }
  }

  /**
   * Load the campaigns relevant to a group: visible campaigns whose
   * source group matches, plus any campaign attached to the group's
   * published marketplace products.
   */
  async function loadCampaignsByGroup(groupId, offers = []) {
    const merged = new Map()
    let visible = []
    try {
      const { data } = await client.get('save-to-buy/campaigns/visible')
      visible = Array.isArray(data) ? data : []
    } catch {
      // offline — local cache only
    }
    visible.forEach((c) => merged.set(c.id, c))

    const productIds = (offers || [])
      .map((o) => o?.id)
      .filter(Boolean)
      .slice(0, 12)
    await Promise.all(
      productIds.map(async (productId) => {
        try {
          const { data } = await client.get(
            `save-to-buy/campaigns/product/${productId}`,
          )
          if (Array.isArray(data)) data.forEach((c) => merged.set(c.id, c))
          else if (data?.id) merged.set(data.id, data)
        } catch {
          // per-product fetch is best effort
        }
      }),
    )

    const filtered = Array.from(merged.values())
      .filter((c) => String(c.sourceGroupId) === String(groupId))
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    filtered.forEach(upsertCampaign)
    return filtered
  }

  async function loadCreatorCampaigns(userId) {
    try {
      const { data } = await client.get(
        `save-to-buy/campaigns/creator/${userId}`,
      )
      ;(Array.isArray(data) ? data : []).forEach(upsertCampaign)
      return data || []
    } catch {
      return []
    }
  }

  async function createCampaign(payload) {
    try {
      const { data } = await client.post('save-to-buy/campaigns', payload)
      upsertCampaign(data)
      activeCampaign.value = data
      return data
    } catch (error) {
      if (!isNetworkError(error)) throw error
      const price = Number(payload.individualPrice) || 0
      const targetQuantity = Number(payload.targetQuantity) || 1
      const campaign = {
        id: localId('stb'),
        ...payload,
        creatorUserId: payload.creatorUserId,
        currentQuantity: 0,
        committedAmount: 0,
        escrowedAmount: 0,
        status: 'DRAFT',
        quantityProgress: 0,
        amountProgress: 0,
        remainingQuantity: targetQuantity,
        remainingAmount: price * targetQuantity,
        hasReachedTarget: false,
        isVisible: false,
        isAcceptingParticipants: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
      upsertCampaign(campaign)
      activeCampaign.value = campaign
      return campaign
    }
  }

  async function updateCampaign(id, payload) {
    const { data } = await client.put(`save-to-buy/campaigns/${id}`, payload)
    upsertCampaign(data)
    activeCampaign.value = data
    return data
  }

  async function campaignAction(path) {
    const { data } = await client.post(`save-to-buy/campaigns/${path}`)
    upsertCampaign(data)
    activeCampaign.value = data
    return data
  }

  function activateCampaign(id) {
    return campaignAction(`${id}/activate`)
  }
  function cancelCampaign(id) {
    return campaignAction(`${id}/cancel`)
  }
  function markCampaignExpired(id) {
    return campaignAction(`${id}/expire`)
  }
  function markTargetReached(id) {
    return campaignAction(`${id}/target-reached`)
  }
  function startAllocation(id) {
    return campaignAction(`${id}/allocate`)
  }
  function markCampaignSuccessful(id) {
    return campaignAction(`${id}/success`)
  }

  // ── Participants ─────────────────────────────────────────────────

  /**
   * Join a campaign (status JOINED). committedAmount = quantity × unit price
   * unless explicitly supplied.
   */
  async function joinCampaign(campaignId, { userId, quantity, committedAmount, paymentMode }) {
    const campaign = campaigns.value.find((c) => c.id === campaignId)
    const unit = Number(campaign?.individualPrice) || 0
    try {
      const { data } = await client.post(
        `save-to-buy/participants/campaigns/${campaignId}`,
        {
          userId,
          quantity: Number(quantity) || 1,
          committedAmount: committedAmount ?? unit * (Number(quantity) || 1),
          paymentMode,
        },
      )
      activeParticipant.value = data
      await upsertParticipation(data)
      return data
    } catch (error) {
      if (!isNetworkError(error)) throw error
      const participant = {
        id: localId('stbp'),
        campaignId,
        userId,
        quantity: Number(quantity) || 1,
        committedAmount: committedAmount ?? unit * (Number(quantity) || 1),
        escrowedAmount: 0,
        finalAmount: 0,
        status: 'JOINED',
        paymentMode: paymentMode || 'COMMITMENT_ONLY',
        joinedAt: new Date().toISOString(),
        totalHeldAmount: 0,
        isActive: true,
        hasFundsHeld: false,
      }
      activeParticipant.value = participant
      await upsertParticipation(participant)
      return participant
    }
  }

  async function getMyParticipant(campaignId, userId) {
    try {
      const { data } = await client.get(
        `save-to-buy/participants/campaigns/${campaignId}/users/${userId}`,
      )
      activeParticipant.value = data
      return data
    } catch (error) {
      if (error?.response?.status === 404) {
        activeParticipant.value = null
        return null
      }
      throw error
    }
  }

  async function getMyParticipations(userId) {
    try {
      const { data } = await client.get(
        `save-to-buy/participants/users/${userId}`,
      )
      myParticipations.value = Array.isArray(data) ? data : []
      return myParticipations.value
    } catch {
      myParticipations.value = []
      return []
    }
  }

  function upsertParticipation(participant) {
    if (!participant?.id) return
    const index = myParticipations.value.findIndex((p) => p.id === participant.id)
    if (index === -1) myParticipations.value.unshift(participant)
    else myParticipations.value[index] = participant
  }

  async function participantAction(path) {
    const { data } = await client.post(`save-to-buy/participants/${path}`)
    activeParticipant.value = data
    upsertParticipation(data)
    return data
  }

  function commitFunds(participantId) {
    return participantAction(`${participantId}/commit`)
  }
  function escrowFunds(participantId) {
    return participantAction(`${participantId}/escrow`)
  }
  function buyAlone(participantId) {
    return participantAction(`${participantId}/buy-alone`)
  }
  function refundParticipant(participantId) {
    return participantAction(`${participantId}/refund`)
  }
  function cancelParticipation(participantId) {
    return participantAction(`${participantId}/cancel`)
  }

  async function updateParticipantQuantity(participantId, quantity) {
    const { data } = await client.put(
      `save-to-buy/participants/${participantId}/quantity`,
      null,
      { params: { quantity } },
    )
    activeParticipant.value = data
    upsertParticipation(data)
    return data
  }

  async function updateParticipantPaymentMode(participantId, paymentMode) {
    const { data } = await client.put(
      `save-to-buy/participants/${participantId}/payment-mode`,
      null,
      { params: { paymentMode } },
    )
    activeParticipant.value = data
    upsertParticipation(data)
    return data
  }

  // ── Contributions / installments ─────────────────────────────────

  async function createContribution(participantId, { installmentNumber, expectedAmount, dueAt }) {
    const { data } = await client.post(
      `save-to-buy/contributions/participants/${participantId}`,
      {
        installmentNumber,
        expectedAmount,
        dueAt: dueAt || new Date(Date.now() + 30 * 86400000).toISOString(),
      },
    )
    if (!contributions.value.some((c) => c.id === data.id)) {
      contributions.value.push(data)
    }
    return data
  }

  /**
   * Split a participant's commitment into `count` monthly installments.
   */
  async function scheduleInstallments(participantId, totalAmount, count = INSTALLMENTS_DEFAULT_MONTHS) {
    const n = Math.max(1, Number(count) || 1)
    const total = Number(totalAmount) || 0
    const base = Math.floor(total / n)
    const created = []
    for (let i = 1; i <= n; i += 1) {
      const amount = i === n ? total - base * (n - 1) : base
      created.push(
        await createContribution(participantId, {
          installmentNumber: i,
          expectedAmount: amount,
          dueAt: new Date(Date.now() + i * 30 * 86400000).toISOString(),
        }),
      )
    }
    return created
  }

  async function getContributions(participantId) {
    try {
      const { data } = await client.get(
        `save-to-buy/contributions/participants/${participantId}`,
      )
      contributions.value = Array.isArray(data) ? data : []
      return contributions.value
    } catch {
      contributions.value = []
      return []
    }
  }

  async function getNextPendingContribution(participantId) {
    try {
      const { data } = await client.get(
        `save-to-buy/contributions/participants/${participantId}/next-pending`,
      )
      return data
    } catch {
      return null
    }
  }

  async function markContributionPaid(contributionId, { paymentReference, amount }) {
    const { data } = await client.post(
      `save-to-buy/contributions/${contributionId}/pay`,
      null,
      { params: { paymentReference, amount } },
    )
    const index = contributions.value.findIndex((c) => c.id === contributionId)
    if (index !== -1) contributions.value[index] = data
    return data
  }

  // ── Demand requests (product wishlist) ───────────────────────────

  async function createDemandRequest(payload) {
    try {
      const { data } = await client.post('demand-requests', payload)
      upsertDemand(data)
      return data
    } catch (error) {
      if (!isNetworkError(error)) throw error
      const demand = {
        id: localId('stbd'),
        ...payload,
        interestedCount: 1,
        status: 'OPEN',
        createdAt: new Date().toISOString(),
        isOpenForInterest: true,
        isActive: true,
      }
      upsertDemand(demand)
      return demand
    }
  }

  async function loadGroupDemands(groupId) {
    try {
      const { data } = await client.get(`demand-requests/groups/${groupId}`)
      ;(Array.isArray(data) ? data : []).forEach(upsertDemand)
      return data || []
    } catch {
      return demandRequests.value.filter(
        (r) => String(r.sourceGroupId) === String(groupId),
      )
    }
  }

  async function joinDemandRequest(demandId, userId) {
    try {
      const { data } = await client.post(
        `demand-requests/${demandId}/interested/${userId}`,
      )
      upsertDemand(data)
      return data
    } catch (error) {
      if (!isNetworkError(error)) throw error
      const demand = demandRequests.value.find((r) => r.id === demandId)
      if (demand) {
        demand.interestedCount = (Number(demand.interestedCount) || 0) + 1
        upsertDemand(demand)
      }
      return demand
    }
  }

  // ── Request-guarded actions (idempotency helper) ─────────────────

  async function runOnce(key, fn) {
    if (requestIds.value.has(key)) return null
    requestIds.value.add(key)
    try {
      return await fn()
    } finally {
      setTimeout(() => requestIds.value.delete(key), 800)
    }
  }

  return {
    campaigns,
    demandRequests,
    activeCampaign,
    activeParticipant,
    myParticipations,
    contributions,
    loading,
    byGroup,
    campaignById,
    getCampaign,
    loadCampaignsByGroup,
    loadCreatorCampaigns,
    createCampaign,
    updateCampaign,
    activateCampaign,
    cancelCampaign,
    markCampaignExpired,
    markTargetReached,
    startAllocation,
    markCampaignSuccessful,
    joinCampaign,
    getMyParticipant,
    getMyParticipations,
    commitFunds,
    escrowFunds,
    buyAlone,
    refundParticipant,
    cancelParticipation,
    updateParticipantQuantity,
    updateParticipantPaymentMode,
    createContribution,
    scheduleInstallments,
    getContributions,
    getNextPendingContribution,
    markContributionPaid,
    createDemandRequest,
    loadGroupDemands,
    joinDemandRequest,
    runOnce,
  }
})